import Skeleton from 'react-loading-skeleton';
import { AIECGViewer, ECGClient } from '@/shared/ui';
import { STRIP_MANAGEMENT_TAB_ID } from '@/entities/report';
import useEcgViewerWrapper from './useEcgViewerWrapper';

const configChart = {
  isDisplayScrollbar: true,
  isEdit: true,
};

export type EcgViewerWrapperProps = {
  isActive: boolean;
  ecgDataMap: any;
  timezoneOffset?: any;
  profileId?: any;
  studyId?: any;
  handleClickArrowButton: (isNext?: boolean) => void;
};

/**
 * Renders the ECG viewer panel of the strips management tab for the currently selected strip. Falls back to a three-part skeleton while useEcgViewerWrapper loads or the ECG object has no samples, otherwise mounts the AI ECG viewer with tab-scoped canvas ids, the strip start moment and arrow-button navigation, and renders nothing unless the tab is active.
 *
 * @param props - Active flag, ECG data map, timezone offset and study context for the viewer.
 * @returns The ECG viewer container, a loading skeleton, or nothing while the tab is inactive.
 */
const EcgViewerWrapper = ({
  isActive,
  ecgDataMap,
  timezoneOffset,
  profileId,
  studyId,
  handleClickArrowButton,
}: EcgViewerWrapperProps) => {
  const {
    isLoading,
    ecgObject,
    startStripEpoch,
    aiChannel,
    isDisableNextBtn,
    isDisablePrevBtn,
    clickArrowButtonHandle,
  } = useEcgViewerWrapper({ ecgDataMap, profileId, studyId, handleClickArrowButton });

  if (isLoading || !ecgObject?.dataSamples?.length || !startStripEpoch.current) {
    return (
      <div id="ecg-viewer" className="ecg-viewer-container">
        <div className="ecg-viewer-skeleton-container">
          <div className="scroller-container">
            <Skeleton height="100%" />
          </div>
          <div className="chart-container">
            <Skeleton height="100%" />
          </div>
          <div className="menu-container">
            <Skeleton height="100%" />
          </div>
        </div>
      </div>
    );
  }

  return (
    isActive && (
      <div id="ecg-viewer" className="ecg-viewer-container">
        <ECGClient>
          <AIECGViewer
            idComponents={{
              canvasBackground: 'cvBgStripManagement',
              exportGridCanvas: 'expGridCvStripManagement',
              canvasChart: 'cvChartStripManagement',
              canvasObject: 'cvObjStripManagement',
              canvasScroller: 'cvScrollerStripManagement',
              contextMenu: 'contextMenuStripManagement',
              tooltip: 'tooltipStripManagement',
              tab: STRIP_MANAGEMENT_TAB_ID,
            }}
            ecg={ecgObject}
            ecgDataMap={ecgDataMap}
            configChart={configChart}
            isAppliedAI
            isDisableAIAssist
            isHideAddRemoveBeatMenu
            isHideFineGridMenu
            isHideMarkASampleMenu
            hasArrowButtonScroller
            handleClickArrowButton={clickArrowButtonHandle}
            timeObject={{
              startMoment: startStripEpoch.current,
              timezoneOffset,
            }}
            aiChannel={aiChannel}
            isDisableNextBtn={isDisableNextBtn}
            isDisablePrevBtn={isDisablePrevBtn}
          />
        </ECGClient>
      </div>
    )
  );
};

export default EcgViewerWrapper;
